import { formatDate } from "@similarweb/pro-logic-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { type Translate, useTranslation } from "pro-features/components/WithTranslation";
import { NO_DATA_SIGN } from "app/filters/const";
import type { IDurationObject } from "app/services/DurationService";

import { type ChartTooltipColumnDefinition, ChartTooltipWrapper } from "../../components/ChartTooltipWrapper";

import type { ChartSeriesItemData } from "./types";
import { changeValueFormatter, shareValueFormatter, volumeFormatter } from "./utils";

interface ChartTooltipData extends ChartSeriesItemData {
    metric: string;
}

const buildColumns = (
    t: Translate,
    { from, to, compareFrom, compareTo }: IDurationObject<string>,
    isYearOverYear: boolean,
) => {
    const isPeriodOverPeriod = compareFrom !== undefined && compareTo !== undefined;
    const columns = [
        {
            key: "metric",
            title: t("demand.analysis.search.trends.map.tooltip.metric"),
            selector: "metric",
            align: "left",
            size: "146px",
            borderRight: true,
            render: ({ metric }) => <ChartTooltip.LabelCell label={metric} />,
        },
        {
            key: "previousVolume",
            title: formatDate(compareFrom, compareTo),
            selector: "previousVolume",
            align: "right",
            size: "50px",
            render: ({ previousVolume }) => (
                <div data-automation="chart-tooltip-volume-value-previous-period">
                    <ChartTooltip.ValueCell value={volumeFormatter(previousVolume)} />
                </div>
            ),
        },
        {
            key: "previousShare",
            title: "",
            selector: "previousShare",
            align: "right",
            size: "50px",
            borderRight: true,
            render: ({ previousShare }) => (
                <div data-automation="chart-tooltip-previous-share-value">
                    <ChartTooltip.ValueCell value={shareValueFormatter(previousShare ?? 0)} />
                </div>
            ),
        },
        {
            key: "currentVolume",
            title: isPeriodOverPeriod ? formatDate(from, to) : "",
            selector: "volume",
            align: "right",
            size: "50px",
            render: ({ volume }) => (
                <div data-automation="chart-tooltip-volume-value-current-period">
                    <ChartTooltip.ValueCell value={volumeFormatter(volume)} />
                </div>
            ),
        },
        {
            key: "currentShare",
            title: isPeriodOverPeriod ? "" : t("common.value"),
            selector: "share",
            align: "right",
            size: "50px",
            borderRight: true,
            render: ({ share }) => (
                <div data-automation="chart-tooltip-share-value">
                    <ChartTooltip.ValueCell value={shareValueFormatter(share)} />
                </div>
            ),
        },
        {
            key: "change",
            get title() {
                return isPeriodOverPeriod
                    ? t("common.tooltip.change.pop", {
                          comparedPeriod: isYearOverYear ? t("common.yoy") : t("common.pop"),
                      })
                    : t("demand.analysis.search.trends.map.tooltip.volume.change");
            },
            selector: "change",
            align: "right",
            size: isPeriodOverPeriod ? "65px" : "90px",
            render: ({ change }) => (
                <div data-automation="chart-tooltip-change-value">
                    {change ? (
                        <ChartTooltip.ChangeCell
                            value={changeValueFormatter(change)}
                            isDecrease={change < 0}
                        />
                    ) : (
                        <ChartTooltip.ValueCell value={NO_DATA_SIGN} />
                    )}
                </div>
            ),
        },
    ] satisfies ChartTooltipColumnDefinition<ChartTooltipData>[];

    if (!isPeriodOverPeriod) {
        return columns.filter(({ selector }) => selector !== "previousVolume" && selector !== "previousShare");
    }

    return columns;
};

interface TrafficByCountryMapTooltipProps {
    title: string;
    data: ChartSeriesItemData[];
    duration: IDurationObject<string>;
    isYearOverYear: boolean;
}

export const TrafficByCountryMapTooltip = ({
    title,
    data,
    duration,
    isYearOverYear,
}: TrafficByCountryMapTooltipProps) => {
    const t = useTranslation();
    const columnDefinition = buildColumns(t, duration, isYearOverYear);
    const metricName = t("demand.analysis.search.trends.map.tooltip.metric.name");

    return (
        <div data-automation="map-tooltip">
            <ChartTooltipWrapper<ChartTooltipData>
                title={title}
                data={data.map((item) => ({ ...item, metric: metricName }))}
                columnDefinition={columnDefinition}
            />
        </div>
    );
};
